import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import SetupBudget from "../../components/pm/SetupBudget";

const msg = (e) => e?.response?.data?.detail?.message || "Something went wrong.";
const BTN = "min-h-[44px] px-4 rounded-sm text-sm font-semibold border border-erf-borderCream focus-visible:outline focus-visible:outline-2 focus-visible:outline-erf-dark";
const IN = "min-h-[44px] rounded border border-erf-borderCream px-3 bg-white text-sm";

// B7: project setup wizard — budget by cost code (from the estimate / conversion), milestones, team (B2b assignments).
export default function ProjectSetup() {
  const { pid } = useParams();
  const [s, setS] = useState(null);
  const [note, setNote] = useState("");
  const [ms, setMs] = useState({ name: "", date: "" });
  const load = useCallback(() => api.get(`/pm/projects/${pid}/setup`).then((r) => setS(r.data)).catch((e) => setNote(msg(e))), [pid]);
  useEffect(() => { load(); }, [load]);
  const run = (p, ok) => p.then(() => { setNote(ok); return load(); }).catch((e) => setNote(msg(e)));
  const addMs = (e) => { e.preventDefault(); run(api.post(`/pm/projects/${pid}/setup/milestones`, ms), "Milestone added.").then(() => setMs({ name: "", date: "" })); };
  return (
    <AppShell title="Project setup" subtitle="Budget, milestones and team">
      <div className="mx-auto w-full max-w-[900px] px-4 md:px-6 space-y-6" data-testid="pm-setup">
        {note && <p role="status" className="text-sm" data-testid="pm-setup-note">{note}</p>}
        {s && (
          <>
            <p className="text-sm" data-testid="pm-setup-source">{s.project.name} · {s.project.source === "crm_conversion" ? "from a converted contract" : "set up manually"}
              {s.project.setup_completed_at && " · setup complete"}</p>
            <SetupBudget s={s} pid={pid} run={run} />
            <section className="space-y-2" data-testid="pm-setup-milestones">
              <h2 className="font-heading text-lg">2. Milestones</h2>
              <ul className="text-sm space-y-1">{s.milestones.map((m) => <li key={m.id} data-testid={`pm-setup-ms-${m.id}`}>{m.planned_start} · {m.name}</li>)}</ul>
              {!s.milestones.length && <p className="text-sm text-erf-dark/70">No milestones yet.</p>}
              {s.can.milestones && (
                <form onSubmit={addMs} className="flex flex-wrap gap-2 items-end">
                  <label className="text-xs flex flex-col">Name<input className={IN} value={ms.name} onChange={(e) => setMs({ ...ms, name: e.target.value })} data-testid="pm-setup-ms-name" /></label>
                  <label className="text-xs flex flex-col">Date<input type="date" className={IN} value={ms.date} onChange={(e) => setMs({ ...ms, date: e.target.value })} data-testid="pm-setup-ms-date" /></label>
                  <button type="submit" className={BTN} data-testid="pm-setup-ms-add">Add milestone</button>
                </form>
              )}
            </section>
            <section className="space-y-2" data-testid="pm-setup-team">
              <h2 className="font-heading text-lg">3. Team</h2>
              <ul className="text-sm space-y-1">{s.team.map((t) => <li key={t.id} data-testid={`pm-setup-team-${t.user_id}`}>{t.name} · {t.role_on_project}</li>)}</ul>
              {s.can.team && <Link to={`/projects/${pid}?tab=team`} className="underline text-sm inline-flex min-h-[44px] items-center" data-testid="pm-setup-team-link">Manage project team</Link>}
            </section>
            <div className="flex flex-wrap gap-2">
              {s.can.complete && <button type="button" className={`${BTN} bg-erf-dark text-white`} onClick={() => run(api.post(`/pm/projects/${pid}/setup/complete`), "Setup marked complete.")} data-testid="pm-setup-complete">Finish setup</button>}
              <Link to={`/pm/projects/${pid}/schedule`} className={`${BTN} inline-flex items-center`} data-testid="pm-setup-schedule-link">Open schedule</Link>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
